import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { submitForm, saveFormProgress, validateFormData } from '../utils/secureFormSubmit';
import { isValidFileType, isValidFileSize, secureLog } from '../config/secure-config';
import { 
  trackFormStep, 
  trackFormField, 
  trackFormSubmission,
  trackFormAbandonment,
  trackFieldEngagement,
  pushToDataLayer
} from '../utils/loadGTM';

const { FiUpload, FiArrowLeft, FiArrowRight, FiInfo, FiCheck, FiX, FiBriefcase, FiTarget, FiMapPin, FiPhone, FiImage, FiShare2, FiUser, FiAlertCircle, FiCalendar, FiGlobe, FiMessageSquare, FiFacebook, FiInstagram, FiLinkedin, FiLoader } = FiIcons;

const InteractiveForm = () => {
  const [step, setStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Info
    businessName: '',
    businessType: '',
    
    // Goals & Target
    mainGoal: '',
    targetAudience: '',
    uniqueValue: '',
    mainServices: '',
    specialOffer: '',
    
    // Location
    hasPhysicalStore: 'no',
    branches: [],
    workingHours: '',
    
    // Contact
    phone: '',
    whatsapp: 'yes',
    whatsappNumber: '',
    
    // Branding
    hasLogo: 'no',
    logo: null,
    colors: [],
    images: [],
    
    // Social Media
    facebook: '',
    instagram: '',
    linkedin: '',
    tiktok: '',
    
    // Personal Contact
    name: '',
    email: '',
    contactPhone: '',
    
    // Hidden
    honeypot: ''
  });

  // Time tracking for GTM
  const [formStartTime] = useState(Date.now());
  const [stepStartTime, setStepStartTime] = useState(Date.now());

  // Progress tracking
  const [showProgressRestore, setShowProgressRestore] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);

  // Load saved progress on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('formProgress');
      if (saved) {
        const parsedData = JSON.parse(saved);
        if (parsedData && parsedData.data) {
          setSavedProgress(parsedData);
          setShowProgressRestore(true);
          setTimeout(() => setShowProgressRestore(false), 10000);
        }
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
    }
  }, []);

  // Auto-save progress
  useEffect(() => {
    const saveTimer = setTimeout(() => {
      if (step > 0 && !isSubmitted) {
        saveFormProgress(formData);
      }
    }, 1000);

    return () => clearTimeout(saveTimer);
  }, [formData, step, isSubmitted]);
  

  const questions = [
    {
      id: 'businessName',
      title: 'מה שם העסק שלך?',
      type: 'text',
      placeholder: 'לדוגמה: סלסלת פירות הזהב',
      icon: FiBriefcase,
      required: true,
      autocomplete: 'organization'
    },
    {
      id: 'businessType',
      title: 'במה העסק עוסק?',
      type: 'textarea',
      placeholder: 'לדוגמה: חנות מתמחה בסלסלאות פירות יוקרתיות, מגשי פירות לאירועים ומארזי פירות לחגים. אנו מציעים משלוחים לכל הארץ',
      required: true
    },
    {
      id: 'mainGoal',
      title: 'מה המטרה העיקרית של העמוד?',
      type: 'radio',
      options: [
        { value: 'leads', label: 'קבלת הזמנות מלקוחות', icon: FiPhone },
        { value: 'sales', label: 'מכירת מוצרים אונליין', icon: FiBriefcase },
        { value: 'booking', label: 'קביעת פגישות להתייעצות', icon: FiCalendar },
        { value: 'signup', label: 'הרשמה למועדון לקוחות', icon: FiUser }
      ],
      required: true
    },
    {
      id: 'targetAudience',
      title: 'מי קהל היעד שלך?',
      type: 'textarea',
      placeholder: 'לדוגמה: משפחות המחפשות מתנות לחגים ואירועים, חברות המעוניינות במארזי פירות לעובדים, אנשים המחפשים אלטרנטיבה בריאה למתנות מתוקות',
      icon: FiTarget,
      required: true
    },
    {
      id: 'uniqueValue',
      title: 'מה מייחד אותך מהמתחרים?',
      type: 'textarea',
      placeholder: 'לדוגמה: פירות טריים שנבחרים ידנית כל בוקר, עיצובים אומנותיים ייחודיים, אחריות על טריות של 48 שעות, משלוח מקורר לכל הארץ',
      required: true
    },
    {
      id: 'mainServices',
      title: 'מה השירותים/מוצרים העיקריים שלך?',
      type: 'textarea',
      placeholder: 'לדוגמה:\n• סלסלאות פירות קלאסיות - 5 גדלים\n• מגשי פירות חתוכים לאירועים\n• מארזי פירות יבשים ואגוזים\n• סלסלאות שוקולד ופירות\n• הכנת סידורים בהתאמה אישית',
      rows: 5,
      required: true
    },
    {
      id: 'specialOffer',
      title: 'האם יש מבצע או הטבה מיוחדת?',
      type: 'textarea',
      placeholder: 'לדוגמה: משלוח חינם בהזמנה מעל 200₪, 10% הנחה להזמנה ראשונה, סלסלה קטנה במתנה בהזמנה מעל 500₪\n\nאם אין מבצע כרגע, השאר ריק',
      required: false
    },
    {
      id: 'hasPhysicalStore',
      title: 'האם יש לעסק מיקום פיזי?',
      type: 'radio',
      options: [
        { value: 'yes', label: 'כן', icon: FiMapPin },
        { value: 'no', label: 'לא - עסק אונליין בלבד', icon: FiGlobe }
      ],
      required: true
    },
    {
      id: 'branches',
      title: 'מה כתובת העסק?',
      type: 'dynamic-list',
      placeholder: 'לדוגמה: רחוב הרצל 50, ראשון לציון',
      condition: (data) => data.hasPhysicalStore === 'yes',
      required: true
    },
    {
      id: 'workingHours',
      title: 'מה שעות הפעילות?',
      type: 'textarea',
      placeholder: 'לדוגמה:\nראשון-חמישי: 8:00-19:00\nשישי: 8:00-14:00\nשבת: סגור',
      condition: (data) => data.hasPhysicalStore === 'yes',
      required: false
    },
    {
      id: 'phone',
      title: 'מספר הטלפון בו הלקוחות שלך יוכלו ליצור קשר',
      type: 'tel',
      placeholder: 'לדוגמה: 050-1234567',
      icon: FiPhone,
      required: true,
      autocomplete: 'tel'
    },
    {
      id: 'whatsapp',
      title: 'האם להוסיף כפתור WhatsApp?',
      type: 'radio',
      options: [
        { value: 'yes', label: 'כן - עם אותו מספר טלפון', icon: FiMessageSquare },
        { value: 'different', label: 'כן - עם מספר אחר', icon: FiPhone },
        { value: 'no', label: 'לא', icon: FiX }
      ],
      required: true
    },
    {
      id: 'whatsappNumber',
      title: 'מספר WhatsApp',
      type: 'tel',
      placeholder: 'לדוגמה: 050-7654321',
      condition: (data) => data.whatsapp === 'different',
      required: true,
      autocomplete: 'tel'
    },
    {
      id: 'hasLogo',
      title: 'האם יש לעסק לוגו?',
      type: 'radio',
      options: [
        { value: 'yes', label: 'כן', icon: FiImage },
        { value: 'no', label: 'לא', icon: FiX }
      ],
      required: true
    },
    {
      id: 'logo',
      title: 'העלה את הלוגו',
      type: 'file',
      accept: 'image/*',
      condition: (data) => data.hasLogo === 'yes',
      required: true
    },
    {
      id: 'colors',
      title: 'בחר צבעים לעיצוב העמוד',
      type: 'color-picker',
      multiple: true,
      required: false
    },
    {
      id: 'images',
      title: 'העלה תמונות להצגה בעמוד',
      type: 'file',
      multiple: true,
      accept: 'image/*',
      required: false
    },
    {
      id: 'socialLinks',
      title: 'קישורים לרשתות חברתיות (אופציונלי)',
      type: 'social-links',
      fields: [
        { id: 'facebook', label: 'פייסבוק', placeholder: 'https://facebook.com/שם-העמוד', icon: FiFacebook },
        { id: 'instagram', label: 'אינסטגרם', placeholder: 'https://instagram.com/שם-המשתמש', icon: FiInstagram },
        { id: 'linkedin', label: 'לינקדאין', placeholder: 'https://linkedin.com/company/שם-החברה', icon: FiLinkedin },
        { id: 'tiktok', label: 'טיקטוק', placeholder: 'https://tiktok.com/@שם-המשתמש', icon: FiShare2 }
      ],
      required: false
    },
    {
      id: 'contactDetails',
      title: 'פרטי איש קשר',
      type: 'contact',
      fields: [
        { id: 'name', type: 'text', placeholder: 'שם מלא', label: 'שם מלא', required: true, autocomplete: 'name' },
        { id: 'email', type: 'email', placeholder: 'כתובת אימייל', label: 'אימייל', required: true, autocomplete: 'email' },
        { id: 'contactPhone', type: 'tel', placeholder: 'טלפון', label: 'טלפון', required: true, autocomplete: 'tel' }
      ],
      required: true
    }
  ];

  const availableQuestions = questions.filter(q => !q.condition || q.condition(formData));
  const currentQuestion = availableQuestions[step];
  const isLastStep = step === availableQuestions.length - 1;

  // Track step changes for GTM
  useEffect(() => {
    if (!isSubmitted && currentQuestion) {
      // Track step view
      trackFormStep(
        step + 1, 
        currentQuestion.title, 
        availableQuestions.length
      );
      
      // Reset step timer
      setStepStartTime(Date.now());
      
      // Track step progression details
      pushToDataLayer({
        event: 'form_progress',
        form_name: 'landing_page_order',
        progress_percentage: Math.round(((step + 1) / availableQuestions.length) * 100),
        time_on_previous_step: Date.now() - stepStartTime,
        current_step_type: currentQuestion.type,
        current_step_required: currentQuestion.required !== false
      });
    }
  }, [step, isSubmitted]);

  // Track form abandonment on unmount
  useEffect(() => {
    const startTime = Date.now();
    
    return () => {
      if (!isSubmitted && step > 0) {
        const timeSpent = Date.now() - startTime;
        const currentQ = availableQuestions[step];
        trackFormAbandonment(step + 1, currentQ?.id || 'unknown', timeSpent);
      }
    };
  }, [isSubmitted, step]);

  const [newBranch, setNewBranch] = useState('');

  const defaultColors = [
    '#2eb8e6', '#4a4a4a', '#ff3b5c', '#9333ea', 
    '#00ffff', '#ffd93d', '#10b981', '#f59e0b'
  ];

  const handleInputChange = (id, value) => {
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    
    // Track field interaction
    trackFormField(id, typeof value, !!value);
  };

  const handleFileUpload = async (id, files) => {
    if (!files || files.length === 0) return;

    try {
      const fileArray = Array.from(files);
      
      // Validate each file
      for (const file of fileArray) {
        if (!isValidFileType(file)) {
          setSubmitStatus({ 
            type: 'error', 
            message: `סוג הקובץ ${file.name} אינו נתמך. אנא השתמש בתמונות בלבד.` 
          });
          return;
        }
        
        if (!isValidFileSize(file)) {
          setSubmitStatus({ 
            type: 'error', 
            message: `הקובץ ${file.name} גדול מדי. הגודל המקסימלי הוא 5MB.` 
          });
          return;
        }
      }
      
      // Store files
      if (currentQuestion.multiple) {
        handleInputChange(id, [...(formData[id] || []), ...fileArray]);
      } else {
        handleInputChange(id, fileArray[0]);
      }
      
      setSubmitStatus({ 
        type: 'success', 
        message: `${fileArray.length} קבצים הועלו בהצלחה` 
      });
    } catch (error) {
      secureLog('Error handling file upload:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'שגיאה בהעלאת הקובץ. אנא נסה שוב.' 
      });
    }
  };

  const validateCurrentStep = () => {
    if (!currentQuestion) return true;
    
    // Special validation for contact fields
    if (currentQuestion.type === 'contact') {
      const missingFields = currentQuestion.fields
        .filter(field => field.required)
        .filter(field => !formData[field.id] || formData[field.id].trim() === '');
      
      if (missingFields.length > 0) {
        return false;
      }
      return true;
    }
    
    // Special validation for social links - always valid as it's optional
    if (currentQuestion.type === 'social-links') {
      return true;
    }
    
    const value = formData[currentQuestion.id];
    
    // Check if field is required
    if (currentQuestion.required !== false) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return false;
      }
      
      if (typeof value === 'string' && value.trim() === '') {
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    secureLog('Starting form submission');
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Track form submission attempt
    pushToDataLayer({
      event: 'form_submission_attempt',
      form_name: 'landing_page_order',
      total_steps_completed: step + 1,
      total_steps: availableQuestions.length,
      form_completion_percentage: Math.round(((step + 1) / availableQuestions.length) * 100)
    });

    try {
      // Use secure form submission
      const result = await submitForm(formData);
      
      if (result.success) {
        // Track successful submission with all relevant data
        trackFormSubmission(formData);
        
        // Track additional conversion data
        pushToDataLayer({
          event: 'conversion',
          conversion_type: 'landing_page_order',
          value: 1000,
          currency: 'ILS',
          // Business details for segmentation
          business_type: formData.businessType || 'not_specified',
          business_name: formData.businessName ? 'provided' : 'not_provided',
          has_physical_store: formData.hasPhysicalStore || 'no',
          main_goal: formData.mainGoal || 'not_specified',
          target_audience: formData.targetAudience || 'not_specified',
          // Contact preferences
          has_whatsapp: formData.whatsapp || 'yes',
          has_logo: formData.hasLogo || 'no',
          // Social presence
          has_social_links: (formData.facebook || formData.instagram || formData.linkedin || formData.tiktok) ? 'yes' : 'no',
          // Form interaction metrics
          form_fill_duration: Date.now() - stepStartTime, // מחייב הוספת stepStartTime בתחילת הטופס
          form_abandonment_count: 0, // מכיוון שהטופס הושלם
          // User provided data flags (without actual PII)
          provided_email: formData.email ? 'yes' : 'no',
          provided_phone: formData.phone ? 'yes' : 'no',
          provided_contact_phone: formData.contactPhone ? 'yes' : 'no',
          provided_special_offer: formData.specialOffer ? 'yes' : 'no',
          provided_unique_value: formData.uniqueValue ? 'yes' : 'no',
          // Branch count if physical store
          branch_count: formData.branches ? formData.branches.length : 0,
          // Color and image preferences
          selected_colors_count: formData.colors ? formData.colors.length : 0,
          uploaded_images_count: formData.images ? formData.images.length : 0
        });
        
        // Track form completion success
        pushToDataLayer({
          event: 'form_complete',
          form_name: 'landing_page_order',
          submission_id: Date.now().toString(), // Unique submission ID
          submission_success: true
        });
        
        setSubmitStatus({ 
          type: 'success', 
          message: result.message 
        });
        
        // Clear saved progress after successful submission
        try {
          localStorage.removeItem('formProgress');
        } catch (e) {
          // Ignore localStorage errors
        }
        
        setTimeout(() => {
          setIsSubmitted(true);
          setSubmitStatus(null);
          
          // Track thank you page view
          pushToDataLayer({
            event: 'virtual_page_view',
            page_name: 'thank_you_page',
            page_category: 'conversion'
          });
        }, 1000);
        
      } else {
        // Track submission error
        pushToDataLayer({
          event: 'form_submission_error',
          form_name: 'landing_page_order',
          error_message: result.error || 'unknown_error',
          error_step: step + 1,
          error_step_name: currentQuestion?.title || 'unknown'
        });
        
        setSubmitStatus({ 
          type: 'error', 
          message: result.error || 'שגיאה בשליחת הטופס. אנא נסה שוב.' 
        });
      }
    } catch (error) {
      secureLog('Submission error:', error);
      
      // Track critical error
      pushToDataLayer({
        event: 'form_critical_error',
        form_name: 'landing_page_order',
        error_type: 'exception',
        error_message: error.message || 'unknown_exception',
        error_step: step + 1
      });
      
      setSubmitStatus({ 
        type: 'error', 
        message: 'אירעה שגיאה בשליחת הטופס. אנא נסה שוב מאוחר יותר.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      if (currentQuestion.type === 'contact') {
        const missingFields = currentQuestion.fields
          .filter(field => field.required)
          .filter(field => !formData[field.id] || formData[field.id].trim() === '')
          .map(field => field.placeholder);
        
        if (missingFields.length > 0) {
          setSubmitStatus({ 
            type: 'error', 
            message: `אנא מלא את השדות הבאים: ${missingFields.join(', ')}` 
          });
        }
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: 'אנא מלא את השדה הנדרש' 
        });
      }
      return;
    }
    
    setSubmitStatus(null);
    
    if (isLastStep) {
      handleSubmit();
    } else {
      setStep(prev => Math.min(availableQuestions.length - 1, prev + 1));
      // Track form step progression
      const nextStep = Math.min(availableQuestions.length - 1, step + 1);
      const nextQuestion = availableQuestions[nextStep];
      trackFormStep(nextStep + 1, nextQuestion?.id || 'unknown', availableQuestions.length);
    }
  };

  const handlePrevious = () => {
    setSubmitStatus(null);
    setStep(prev => Math.max(0, prev - 1));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // בדוק אם המשתמש בתוך textarea
      const activeElement = document.activeElement;
      const isTextareaFocused = activeElement && 
        activeElement.tagName === 'TEXTAREA' && 
        activeElement === document.activeElement;
      
      if (isTextareaFocused) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handleNext(); 
          break;
          
        case 'ArrowRight':
          e.preventDefault();
          if (step > 0) {
            handlePrevious();
          }
          break;
          
        case 'Enter':
          e.preventDefault();
          handleNext(); // משתמש בפונקציה הקיימת שכוללת ולידציה
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, handleNext, handlePrevious, availableQuestions.length]);



  const restoreProgress = () => {
    if (savedProgress && savedProgress.data) {
      setFormData(savedProgress.data);
      setShowProgressRestore(false);
    }
  };

  const renderInput = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case 'text':
      case 'tel':
      case 'email':
        return (
          <motion.input
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            type={currentQuestion.type}
            value={formData[currentQuestion.id] || ''}
            onChange={(e) => handleInputChange(currentQuestion.id, e.target.value)}
            placeholder={currentQuestion.placeholder}
            autoComplete={currentQuestion.autocomplete || 'off'}
            className="w-full px-6 py-4 bg-white/10 border border-sitechs-blue/30 rounded-xl focus:ring-2 focus:ring-sitechs-blue focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm transition-all hover:bg-white/15"
            required={currentQuestion.required !== false}
          />
        );

      case 'textarea':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-2"
          >
            <motion.textarea
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              value={formData[currentQuestion.id] || ''}
              onChange={(e) => handleInputChange(currentQuestion.id, e.target.value)}
              placeholder={currentQuestion.placeholder}
              className="w-full px-6 py-4 bg-white/10 border border-sitechs-blue/30 rounded-xl focus:ring-2 focus:ring-sitechs-blue focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm min-h-[120px] transition-all hover:bg-white/15 resize-none"
              rows={currentQuestion.rows || 4}
              required={currentQuestion.required !== false}
            />
            <p className="text-xs text-gray-400 mr-2">
              {formData[currentQuestion.id]?.length || 0} תווים
            </p>
          </motion.div>
        );

      case 'radio':
        return (
          <div className="space-y-4">
            {currentQuestion.options.map((option, index) => (
              <motion.label 
                key={option.value} 
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 space-x-reverse p-4 bg-white/10 rounded-xl border border-sitechs-blue/30 hover:border-sitechs-blue cursor-pointer transition-all group hover:bg-white/15"
              >
                <input
                  type="radio"
                  value={option.value}
                  checked={formData[currentQuestion.id] === option.value}
                  onChange={(e) => handleInputChange(currentQuestion.id, e.target.value)}
                  className="form-radio text-sitechs-blue focus:ring-sitechs-blue"
                />
                <div className="flex items-center gap-3 flex-1">
                  {option.icon && (
                    <SafeIcon icon={option.icon} className="text-sitechs-blue text-xl" />
                  )}
                  <span className="text-white group-hover:text-sitechs-blue transition-colors">
                    {option.label}
                  </span>
                </div>
              </motion.label>
            ))}
          </div>
        );

      case 'file':
        return (
          <div className="space-y-4">
            <motion.label
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="block"
            >
              <input
                type="file"
                accept={currentQuestion.accept}
                multiple={currentQuestion.multiple}
                onChange={(e) => handleFileUpload(currentQuestion.id, e.target.files)}
                className="hidden"
                id={`file-${currentQuestion.id}`}
              />
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-dashed border-sitechs-blue/50 rounded-xl p-8 text-center cursor-pointer hover:border-sitechs-blue transition-all hover:bg-white/5"
              >
                <SafeIcon icon={FiUpload} className="mx-auto text-4xl text-sitechs-blue mb-4" />
                <p className="text-white mb-2">
                  {currentQuestion.multiple ? 'העלה קבצים' : 'העלה קובץ'}
                </p>
                <p className="text-sm text-gray-400">
                  לחץ לבחירה או גרור קבצים לכאן
                </p>
              </motion.div>
            </motion.label>
            
            {/* תצוגה מקדימה של קבצים שהועלו - לוגו בודד */}
            {!currentQuestion.multiple && formData[currentQuestion.id] && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative bg-white/10 rounded-xl p-4 border border-sitechs-blue/30"
              >
                <div className="flex items-center gap-4">
                  {/* תצוגה מקדימה של התמונה */}
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-white/10 flex-shrink-0">
                    <img 
                      src={URL.createObjectURL(formData[currentQuestion.id])}
                      alt="תצוגה מקדימה"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }}
                    />
                  </div>
                  
                  {/* פרטי הקובץ */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {formData[currentQuestion.id].name}
                    </p>
                    <p className="text-sm text-gray-400">
                      {(formData[currentQuestion.id].size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  
                  {/* כפתור מחיקה */}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleInputChange(currentQuestion.id, null);
                    }}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                    title="הסר קובץ"
                  >
                    <SafeIcon icon={FiX} className="text-xl" />
                  </motion.button>
                </div>
              </motion.div>
            )}
            
            {/* תצוגה מקדימה של קבצים מרובים - תמונות העסק */}
            {currentQuestion.multiple && formData[currentQuestion.id]?.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-3"
              >
                <p className="text-sm text-gray-400">
                  {formData[currentQuestion.id].length} קבצים נבחרו:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {formData[currentQuestion.id].map((file, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="relative group"
                    >
                      {/* תמונה */}
                      <div className="aspect-square rounded-lg overflow-hidden bg-white/10 border border-sitechs-blue/30">
                        <img 
                          src={URL.createObjectURL(file)}
                          alt={`תמונה ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                          }}
                        />
                      </div>
                      
                      {/* כפתור מחיקה */}
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.preventDefault();
                          const newFiles = formData[currentQuestion.id].filter((_, i) => i !== index);
                          handleInputChange(currentQuestion.id, newFiles.length > 0 ? newFiles : null);
                        }}
                        className="absolute top-2 left-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                        title="הסר תמונה"
                      >
                        <SafeIcon icon={FiX} className="text-sm" />
                      </motion.button>
                      
                      {/* שם הקובץ */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-xs text-white truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-300">
                          {(file.size / 1024).toFixed(0)} KB
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* כפתור להוספת עוד תמונות */}
                <motion.label
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="block"
                >
                  <input
                    type="file"
                    accept={currentQuestion.accept}
                    multiple={currentQuestion.multiple}
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const newFiles = Array.from(e.target.files);
                        handleFileUpload(currentQuestion.id, newFiles);
                      }
                    }}
                    className="hidden"
                  />
                  <div className="border border-dashed border-sitechs-blue/30 rounded-lg p-4 text-center cursor-pointer hover:border-sitechs-blue hover:bg-white/5 transition-all">
                    <div className="flex items-center justify-center gap-2 text-sitechs-blue">
                      <SafeIcon icon={FiUpload} />
                      <span className="text-sm">הוסף עוד תמונות</span>
                    </div>
                  </div>
                </motion.label>
              </motion.div>
            )}
          </div>
        );

      case 'color-picker':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              {defaultColors.map((color, index) => (
                <motion.button
                  key={color}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    const colors = formData.colors || [];
                    if (colors.includes(color)) {
                      handleInputChange('colors', colors.filter(c => c !== color));
                    } else {
                      handleInputChange('colors', [...colors, color]);
                    }
                  }}
                  className={`h-16 rounded-xl transition-all ${
                    formData.colors?.includes(color) 
                      ? 'ring-4 ring-white scale-110' 
                      : 'hover:scale-110'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            {formData.colors?.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap gap-2 mt-4"
              >
                {formData.colors.map((color, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 rounded-full text-white text-sm flex items-center gap-2"
                    style={{ backgroundColor: color }}
                  >
                    {color}
                    <button
                      onClick={() => {
                        handleInputChange(
                          'colors',
                          formData.colors.filter(c => c !== color)
                        );
                      }}
                      className="hover:scale-125 transition-transform"
                    >
                      <SafeIcon icon={FiX} className="text-xs" />
                    </button>
                  </span>
                ))}
              </motion.div>
            )}
          </div>
        );

      case 'dynamic-list':
        return (
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newBranch}
                onChange={(e) => setNewBranch(e.target.value)}
                placeholder={currentQuestion.placeholder}
                className="flex-1 px-6 py-4 bg-white/10 border border-sitechs-blue/30 rounded-xl focus:ring-2 focus:ring-sitechs-blue focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm hover:bg-white/15"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newBranch.trim()) {
                    e.preventDefault();
                    handleInputChange('branches', [...(formData.branches || []), newBranch]);
                    setNewBranch('');
                  }
                }}
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (newBranch.trim()) {
                    handleInputChange('branches', [...(formData.branches || []), newBranch]);
                    setNewBranch('');
                  }
                }}
                className="px-6 py-4 bg-sitechs-blue rounded-xl text-white font-medium hover:bg-sitechs-darkblue transition-colors"
              >
                הוסף
              </motion.button>
            </div>
            {formData.branches?.length > 0 && (
              <motion.div className="space-y-2">
                {formData.branches.map((branch, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-4 bg-white/10 rounded-xl border border-sitechs-blue/30"
                  >
                    <span className="text-white">{branch}</span>
                    <button
                      onClick={() => {
                        handleInputChange(
                          'branches',
                          formData.branches.filter((_, i) => i !== index)
                        );
                      }}
                      className="text-accent-pink hover:scale-125 transition-transform"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            {currentQuestion.fields.map((field, index) => (
              <motion.div
                key={field.id}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="space-y-2"
              >
                {field.label && (
                  <label className="text-sm text-gray-300 block mr-1">
                    {field.label}
                    {field.required && <span className="text-accent-pink mr-1">*</span>}
                  </label>
                )}
                <input
                  type={field.type}
                  placeholder={field.placeholder}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  autoComplete={field.autocomplete || 'off'}
                  className="w-full px-6 py-4 bg-white/10 border border-sitechs-blue/30 rounded-xl focus:ring-2 focus:ring-sitechs-blue focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm hover:bg-white/15"
                  required={field.required}
                />
              </motion.div>
            ))}
          </div>
        );

      case 'social-links':
        return (
          <div className="space-y-4">
            {currentQuestion.fields.map((field, index) => (
              <motion.div
                key={field.id}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="space-y-2"
              >
                <label className="text-sm text-gray-300 flex items-center gap-2">
                  {field.icon && <SafeIcon icon={field.icon} className="text-sitechs-blue" />}
                  {field.label}
                </label>
                <input
                  type="url"
                  placeholder={field.placeholder}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  className="w-full px-6 py-4 bg-white/10 border border-sitechs-blue/30 rounded-xl focus:ring-2 focus:ring-sitechs-blue focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm hover:bg-white/15"
                />
              </motion.div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-2 md:px-4">
      {/* Honeypot Field (Hidden) */}
      <div style={{ position: 'absolute', left: '-9999px' }}>
        <input 
          type="text" 
          name="honeypot" 
          value={formData.honeypot}
          onChange={(e) => handleInputChange('honeypot', e.target.value)}
          tabIndex="-1" 
          autoComplete="off"
          aria-hidden="true"
        />
      </div>


      {/* Progress Restore Notification */}
      {showProgressRestore && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-4 p-4 bg-sitechs-blue/20 border border-sitechs-blue/50 rounded-xl flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiInfo} className="text-sitechs-blue" />
            <span className="text-white">נמצאו נתונים שמורים. האם לשחזר?</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={restoreProgress}
              className="px-4 py-2 bg-sitechs-blue text-white rounded-lg hover:bg-sitechs-darkblue transition-colors"
            >
              שחזר
            </button>
            <button
              onClick={() => setShowProgressRestore(false)}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              התחל מחדש
            </button>
          </div>
        </motion.div>
      )}

      {isSubmitted ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/10 backdrop-blur-xl p-12 rounded-3xl shadow-2xl border border-sitechs-blue/30 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 600, damping: 10 }}
            className="inline-flex items-center justify-center w-24 h-24 bg-green-500/20 rounded-full mb-6"
          >
            <motion.div
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <SafeIcon icon={FiCheck} className="text-5xl text-green-400" />
            </motion.div>
          </motion.div>
          <h3 className="text-3xl font-bold text-white mb-4">תודה רבה!</h3>
          <p className="text-lg text-gray-300 mb-2">קיבלנו את כל הפרטים שלך</p>
          <p className="text-gray-400">ניצור איתך קשר תוך 24 שעות להתחלת העבודה על עמוד הנחיתה שלך</p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 p-4 bg-sitechs-blue/20 rounded-xl"
          >
            <p className="text-sm text-sitechs-blue">
              💡 בינתיים, אתה מוזמן לבדוק דוגמאות של עמודי נחיתה שיצרנו
            </p>
          </motion.div>
          
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            onClick={() => {
              // Reset everything
              setIsSubmitted(false);
              setStep(0);
              setSubmitStatus(null);
              setFormData({
                businessName: '',
                businessType: '',
                mainGoal: '',
                targetAudience: '',
                uniqueValue: '',
                mainServices: '',
                specialOffer: '',
                hasPhysicalStore: 'no',
                branches: [],
                workingHours: '',
                phone: '',
                whatsapp: 'yes',
                whatsappNumber: '',
                hasLogo: 'no',
                logo: null,
                colors: [],
                images: [],
                facebook: '',
                instagram: '',
                linkedin: '',
                tiktok: '',
                name: '',
                email: '',
                contactPhone: '',
                honeypot: ''
              });
            }}
            className="mt-6 text-sm text-gray-400 hover:text-white transition-colors underline"
          >
            רוצה לשלוח טופס נוסף?
          </motion.button>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/10 backdrop-blur-xl p-6 md:p-8 rounded-3xl shadow-2xl border border-sitechs-blue/30"
        >
          {/* Progress Bar with Preview Button */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
              <motion.h3 
                key={step}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-lg md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue to-accent-cyan flex items-center gap-3 order-1"
              >
                {currentQuestion?.icon && (
                  <SafeIcon icon={currentQuestion.icon} className="text-sitechs-blue flex-shrink-0" />
                )}
                <span className="leading-tight">{currentQuestion?.title}</span>
              </motion.h3>
              <div className="flex items-center gap-2 order-2 self-end md:self-auto">
                <span className="text-xs md:text-sm text-gray-400 bg-white/10 px-3 py-1 rounded-full">
                  {step + 1} / {availableQuestions.length}
                </span>
                {isLastStep && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-medium"
                  >
                    שלב אחרון!
                  </motion.div>
                )}
              </div>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden backdrop-blur-sm">
              <motion.div 
                className="h-full bg-gradient-to-r from-sitechs-blue to-accent-cyan relative"
                initial={{ width: 0 }}
                animate={{ width: `${((step + 1) / availableQuestions.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className="absolute right-0 top-0 bottom-0 w-4 bg-white/50 blur-sm"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              </motion.div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="min-h-[200px] mb-8"
            >
              {/* Show required field indicator */}
              {currentQuestion?.required !== false && currentQuestion?.type !== 'contact' && currentQuestion?.type !== 'social-links' && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-xs text-gray-400 mb-4 mr-1"
                >
                  <span className="text-accent-pink">*</span> שדה חובה
                </motion.p>
              )}
              {renderInput()}
            </motion.div>
          </AnimatePresence>

          {/* Status Messages */}
          {submitStatus && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-4 p-3 rounded-xl text-sm ${
                submitStatus.type === 'error' 
                  ? 'bg-red-500/20 border border-red-500/50 text-red-400' 
                  : 'bg-green-500/20 border border-green-500/50 text-green-400'
              }`}
            >
              <div className="flex items-start gap-2">
                <SafeIcon 
                  icon={submitStatus.type === 'error' ? FiAlertCircle : FiCheck} 
                  className="mt-0.5 flex-shrink-0" 
                />
                <div>{submitStatus.message}</div>
              </div>
            </motion.div>
          )}

          <div className="flex justify-between items-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePrevious}
              disabled={step === 0 || isSubmitting}
              className="flex items-center px-4 md:px-6 py-3 text-gray-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors bg-white/10 rounded-full hover:bg-white/15"
            >
              <SafeIcon icon={FiArrowRight} className="ml-2" />
              <span className="hidden md:inline">הקודם</span>
              <span className="md:hidden">חזור</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              disabled={isSubmitting}
              className="flex items-center px-4 md:px-6 py-3 bg-gradient-to-r from-sitechs-blue to-sitechs-darkblue text-white rounded-full font-bold hover:shadow-neon transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="ml-2"
                  >
                    <SafeIcon icon={FiLoader} />
                  </motion.div>
                  שולח...
                </>
              ) : isLastStep ? (
                <>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <SafeIcon icon={FiCheck} className="ml-2" />
                  </motion.div>
                  סיום והזמנה
                </>
              ) : (
                <>
                  <span className="hidden md:inline">הבא</span>
                  <span className="md:hidden">המשך</span>
                  <motion.span
                    animate={{ x: [0, -5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="mr-2"
                  >
                    <SafeIcon icon={FiArrowLeft} />
                  </motion.span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      )}

      {!isSubmitted && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-sitechs-blue/10 backdrop-blur-sm p-6 rounded-2xl border border-sitechs-blue/30"
        >
          <div className="flex items-center text-sitechs-blue">
            <SafeIcon icon={FiInfo} className="ml-3 text-xl flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-300">
                המידע שתספק יעזור לנו ליצור עמוד נחיתה מותאם אישית שימשוך לקוחות חדשים לעסק שלך
              </p>
              <p className="text-xs text-gray-400 mt-2">
                💡 טיפ: הנתונים שלך נשמרים אוטומטית. אם תצא מהעמוד, תוכל להמשיך מאיפה שהפסקת
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default InteractiveForm;